import { Button, Center, Stack, Text, ThemeIcon, Title } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { isRouteErrorResponse, useRouteError } from 'react-router';

import { FullPageMessage } from '@/components/FullPage';

import { NotFoundPage } from './NotFoundPage';

/**
 * `errorElement` of the router: render crashes and failed lazy chunks (typically after a deploy).
 * Inside the shell it replaces only the page; `fullPage` is the last-resort app-level variant.
 */
export function RouteErrorBoundary({ fullPage = false }: { fullPage?: boolean }) {
  const error = useRouteError();
  const { t } = useTranslation();

  useEffect(() => {
    console.error(error);
  }, [error]);

  if (!fullPage && isRouteErrorResponse(error) && error.status === 404) return <NotFoundPage />;

  const reload = () => window.location.reload();

  if (fullPage) {
    return (
      <FullPageMessage
        title={t('page.errorTitle')}
        text={t('page.errorText')}
        actionLabel={t('actions.reload')}
        onAction={reload}
      />
    );
  }

  return (
    <Center py={96}>
      <Stack align="center" gap="sm" maw={460} ta="center">
        <ThemeIcon size={48} radius="xl" variant="light" color="orange">
          <IconAlertTriangle size={26} stroke={1.6} />
        </ThemeIcon>
        <Title order={3}>{t('page.errorTitle')}</Title>
        <Text c="dimmed">{t('page.errorText')}</Text>
        <Button mt="xs" onClick={reload}>
          {t('actions.reload')}
        </Button>
      </Stack>
    </Center>
  );
}
